export interface StoryScene {
  id: string
  title: string
  narrative: string
  mood: 'mystisch' | 'dramatisch' | 'friedlich' | 'dunkel'
  illustrationPrompt: string
  illustrationId?: number      // Storage API ID for scene illustration
  videoId?: number             // Storage API ID for HLS video (replaces illustration)
  audioId?: number             // Storage API ID for TTS narration audio
  location?: string            // POI name reference
}

export interface Story {
  id: string
  title: string
  subtitle: string
  narrator: 'tschauko' | 'tschauka'
  cover: string                // emoji or placeholder for cover art
  coverImageId?: number        // Storage API ID for cover image (overrides scene lookup)
  coverMascots?: ('tschauko' | 'tschauka')[]  // Show mascot videos as cover
  color: string                // accent color for this story
  region: string
  duration: string             // estimated reading/listening time
  scenes: StoryScene[]
  availableLanguages?: string[] // languages available for this story
  /** Sprache, in der die Fassung TATSAECHLICH geliefert wurde. Weicht sie von
   *  der gewaehlten ab, hat `fetchStory` auf Deutsch zurueckgegriffen — der
   *  Leser soll das erfahren, statt unbemerkt eine fremde Sprache zu lesen. */
  language?: string
  postId?: number              // content-api post ID
}

/** Fallback data used when content-api is unreachable */
export const FALLBACK_STORIES: Story[] = [
  {
    id: 'sage-tscheppaschlucht',
    title: 'Der Geist der Schlucht',
    subtitle: 'Eine Sage aus der Tscheppaschlucht',
    narrator: 'tschauko',
    cover: '🌊',
    color: '#7CB342',
    region: 'Tscheppaschlucht',
    duration: '8 Min.',
    scenes: [
      {
        id: 'scene-1',
        title: 'Der alte Köhler',
        narrative: 'Vor langer Zeit, als die Wälder um Ferlach noch dicht und undurchdringlich waren, lebte am Eingang der Tscheppaschlucht ein alter Köhler namens Jakob. Jeden Morgen stieg er hinab in die Schlucht, um Holz zu sammeln. Die anderen Dorfbewohner mieden die Schlucht — man erzählte sich, dass in den Felsspalten ein Geist hauste, der das Wasser bewachte.',
        mood: 'mystisch',
        illustrationPrompt: 'Old charcoal burner at dawn, entering a misty limestone gorge, dark forest, atmospheric watercolor style',
        illustrationId: 103242,
        videoId: 103271,
        location: 'Besucherzentrum',
      },
      {
        id: 'scene-2',
        title: 'Die Stimme im Wasser',
        narrative: 'Eines Herbstabends, als Jakob tiefer in die Schlucht vordrang als je zuvor, hörte er eine Stimme. Sie kam aus dem Wasserfall — nicht laut, nicht leise, sondern wie ein Flüstern das direkt in seinem Kopf erklang. "Wer den Stein des Wassers findet, dem gehört die Schlucht für immer." Jakob erstarrte. Der Loiblbach toste um seine Füße, und im Sprühnebel des Tschaukofalls glaubte er ein Gesicht zu erkennen.',
        mood: 'dramatisch',
        illustrationPrompt: 'Ghostly face forming in waterfall mist, old man standing in gorge stream, dramatic lighting, watercolor',
        illustrationId: 103243,
        location: 'Tschaukofall',
      },
      {
        id: 'scene-3',
        title: 'Die Teufelsbrücke',
        narrative: 'Von da an kehrte Jakob jede Nacht zurück. Er suchte den Stein des Wassers — einen Kristall, so blau wie der tiefste Bergsee. Die Suche führte ihn über die Teufelsbrücke, jenen schmalen Felsbogen, von dem man sagte, der Teufel selbst habe ihn in einer einzigen Nacht errichtet. Hier, in der Mitte der Brücke, wo der Abgrund links und rechts in die Dunkelheit fiel, spürte Jakob den Atem des Berges auf seiner Haut.',
        mood: 'dunkel',
        illustrationPrompt: 'Narrow natural stone bridge over deep dark gorge at night, single lantern light, dramatic depth, watercolor',
        illustrationId: 103245,
        location: 'Teufelsbrücke',
      },
      {
        id: 'scene-4',
        title: 'Das Felsentor',
        narrative: 'Am siebten Tag fand er ihn. Nicht im Wasser, nicht im Fels — sondern eingewachsen in die Wurzeln einer uralten Buche, direkt vor dem Felsentor. Der Stein pulsierte in einem sanften Blau, als hätte er einen eigenen Herzschlag. Als Jakob ihn berührte, verstummte der Bach. Die Vögel schwiegen. Selbst der Wind hielt den Atem an.',
        mood: 'mystisch',
        illustrationPrompt: 'Glowing blue crystal embedded in tree roots near a natural stone arch, magical forest atmosphere, watercolor',
        illustrationId: 103244,
        location: 'Felsentor',
      },
      {
        id: 'scene-5',
        title: 'Der Pakt',
        narrative: '"Nimm ihn nicht", flüsterte die Stimme des Wassers. "Lass ihn wo er ist, und die Schlucht wird für alle da sein — für jede Kröte, jeden Farn, jeden Wanderer. Nimm ihn, und sie gehört dir allein. Aber allein ist kalt, Jakob. Kälter als der tiefste Winter." Jakob sah den Stein an. Dann legte er ihn zurück zwischen die Wurzeln. Und der Bach begann wieder zu fließen.',
        mood: 'friedlich',
        illustrationPrompt: 'Old man gently placing glowing stone back into tree roots, warm golden light, peaceful forest, watercolor',
        illustrationId: 103247,
      },
      {
        id: 'scene-6',
        title: 'Das Vermächtnis',
        narrative: 'Man sagt, der Stein liegt noch immer dort. Und wer aufmerksam genug durch die Tscheppaschlucht wandert — wer den Kröten lauscht und die Farne betrachtet und das Wasser über die Felsen springen sieht — der spürt manchmal ein sanftes blaues Leuchten zwischen den Wurzeln. Jakobs Geschenk an alle, die nach ihm kommen.',
        mood: 'friedlich',
        illustrationPrompt: 'Panoramic view of lush green gorge with subtle blue glow between tree roots, hikers in distance, golden hour, watercolor',
        illustrationId: 103246,
        location: 'Deutscher Peter',
      },
    ],
  },
  {
    id: 'sage-tschauko',
    title: 'Wie Tschauko seine Stimme fand',
    subtitle: 'Die Geschichte der Schlucht-Maskottchen',
    narrator: 'tschauka',
    cover: '🦉',
    coverMascots: ['tschauko', 'tschauka'],
    color: '#4FC3F7',
    region: 'Tscheppaschlucht',
    duration: '5 Min.',
    scenes: [
      {
        id: 'tk-1',
        title: 'Die stille Eule',
        narrative: 'Tschauko war die stillste Eule in der ganzen Schlucht. Während die anderen Vögel sangen und zwitscherten, saß er hoch oben auf seinem Ast über dem Wasserfall und hörte zu. Nicht weil er nicht konnte — sondern weil er glaubte, nichts Wichtiges zu sagen zu haben. Seine großen grünen Augen sahen alles: jeden Farn, jeden Käfer, jeden Tropfen, der den Felsen hinabglitt. Aber sein Schnabel blieb geschlossen.',
        mood: 'friedlich',
        illustrationPrompt: 'Small shy owl sitting alone on a high branch above a gorge waterfall at dusk, other birds singing below, the owl silent and watchful with big green eyes, atmospheric watercolor in forest greens and twilight purples',
        illustrationId: 103262,
        location: 'Tschaukofall',
      },
      {
        id: 'tk-2',
        title: 'Die Wanderin',
        narrative: 'Eines Abends kam eine Wanderin in die Schlucht. Sie setzte sich auf einen Stein, müde vom langen Weg, und blickte nach oben. Dort, im letzten Licht des Tages, sah sie Tschauko auf seinem Ast. "Was für ein wunderschönes Tier", flüsterte sie. "Ich wünschte, jemand könnte mir erzählen, was du alles siehst von dort oben." In diesem Moment spürte Tschauko ein Kribbeln in seiner Kehle — warm und drängend, wie ein Lied, das endlich raus will.',
        mood: 'mystisch',
        illustrationPrompt: 'Female hiker looking up in wonder at a small owl perched on a branch in a limestone gorge, warm afternoon light filtering through trees, magical moment of connection, gentle watercolor illustration',
        illustrationId: 103263,
      },
      {
        id: 'tk-3',
        title: 'Die erste Geschichte',
        narrative: 'Und dann sprach er. Nicht krächzend, nicht kreischend — sondern mit einer warmen, tiefen Stimme, die durch die Schlucht hallte wie der Klang einer alten Glocke. Er erzählte der Wanderin alles: von der Hornotter, die sich zwischen den Steinen sonnt, vom Feuersalamander im feuchten Moos, von den Orchideen, die nur hier wachsen, und von den Geheimnissen, die der Loiblbach seit Jahrhunderten mit sich trägt. Die Wanderin hörte staunend zu. Als sie ging, nahm sie die Geschichten mit in die Welt.',
        mood: 'friedlich',
        illustrationPrompt: 'Owl speaking from a branch with warm golden glow around it, a hiker sitting below listening in amazement, forest creatures gathering to listen, magical storytelling scene in a gorge, charming watercolor with warm greens and golds',
        illustrationId: 103264,
        location: 'Tscheppaschlucht',
      },
      {
        id: 'tk-4',
        title: 'Zwei Stimmen',
        narrative: 'Bald gesellte sich Tschauka dazu — eine kleine Eule mit himmelblauen Augen und einer Stimme wie ein klarer Bergbach. Tschauko erzählt von dem, was man von oben sieht — den weiten Blick, die Zusammenhänge, das große Bild. Tschauka erzählt von dem, was man unten findet — die Details, die Spuren im Moos, das Leben am Boden. Zusammen erzählen sie seither jedem, der die Schlucht besucht, ihre Geschichten. Und die Schlucht wurde lebendig.',
        mood: 'friedlich',
        illustrationPrompt: 'Two owls side by side on a mossy rock in a forest gorge, one larger owl with green eyes and one smaller owl with blue eyes, hikers gathered around listening, warm magical forest atmosphere with dappled light, watercolor illustration',
        illustrationId: 103309,
      },
    ],
  },
  {
    id: 'sage-lindwurm',
    title: 'Der Lindwurm von Klagenfurt',
    subtitle: 'Kärntens berühmteste Drachensage',
    narrator: 'tschauko',
    cover: '🐉',
    color: '#7CB342',
    region: 'Klagenfurt',
    duration: '7 Min.',
    scenes: [
      {
        id: 'lw-1',
        title: 'Das Moor',
        narrative: 'Dort, wo heute die Türme von Klagenfurt in den Himmel ragen, war einst nichts als Sumpf. Endloses, schwarzes Moor. Nebelschwaden krochen über das stille Wasser, und zwischen den Schilfrohren lauerte etwas — ein Lindwurm. Ein Drache, geboren aus dem Schlamm der Urzeit. Sein Körper war so lang wie zehn Ochsenkarren, sein Maul so weit, dass ein Kalb darin verschwand wie ein Brotkrümel im Fluss. Rinder verschwanden über Nacht. Und eines Tages verschwand auch der erste Mensch. Ein Holzfäller. Man fand nur noch seine Axt, halb versunken im Schlamm.',
        mood: 'dunkel',
        illustrationPrompt: 'Dark misty swampland at twilight, a massive dragon silhouette lurking in the reeds, glowing eyes in the fog, watercolor style with deep greens and blacks',
        illustrationId: 103249,
        videoId: 103266,
        location: 'Klagenfurter Becken',
      },
      {
        id: 'lw-2',
        title: 'Die gescheiterten Krieger',
        narrative: 'Herzog Karast sandte seine besten Männer aus. Bewaffnet mit Schwertern und Speeren zogen sie ins Moor. Aber der Sumpfboden gab unter ihren Füßen nach, ihre Rüstungen zogen sie in den Morast, und der Lindwurm war schneller als gedacht. Von den zwölf Männern kehrten acht zurück. Der Herzog war verzweifelt. Aber unter seinen Beratern war ein alter Schmied mit rußgeschwärzten Händen und einem Verstand, schärfer als jede Klinge. "Wir können seine Gier gegen es verwenden", sagte er.',
        mood: 'dramatisch',
        illustrationPrompt: 'Medieval soldiers struggling in dark swampland, sinking into mud, a dragon shadow looming in mist, an old blacksmith with knowing eyes, watercolor',
        illustrationId: 103251,
      },
      {
        id: 'lw-3',
        title: 'Die Falle',
        narrative: 'Die Männer bauten einen Turm aus schweren Steinen am Rand des Moors. Dann nahmen sie den fettesten Stier und trieben eiserne Haken in sein Fleisch — scharf wie Angelhaken, aber groß wie Sicheln. Sie banden den Stier mit Ketten an den Turm und warteten. Kurz vor der Morgendämmerung bebte die Erde. Der Lindwurm kam. Sein Maul schloss sich um den Stier — und die eisernen Haken bohrten sich tief in sein Zahnfleisch. Er brüllte auf, ein Laut wie berstender Fels. Aber die Haken hielten, die Ketten hielten, der Turm hielt.',
        mood: 'dramatisch',
        illustrationPrompt: 'Stone tower at moor edge at dawn, chained bull, dragon biting down with iron hooks in mouth, chains taut, dramatic action, watercolor',
        illustrationId: 103255,
      },
      {
        id: 'lw-4',
        title: 'Der Sieg',
        narrative: 'Dann stürmten die Männer aus ihren Verstecken. Mit eisernen Keulen schlugen sie auf das gefangene Tier ein. Der Kampf dauerte bis die Sonne aufging. Der Lindwurm wand sich, peitschte mit seinem Schwanz — aber er konnte sich nicht befreien. Schlag um Schlag brachen sie seinen Widerstand, bis das Tier endlich still lag. Der Sumpf färbte sich rot. Und dann war Stille.',
        mood: 'dramatisch',
        illustrationPrompt: 'Brave men with iron clubs surrounding a trapped dragon at sunrise, beast writhing in chains, morning light through mist, epic battle, watercolor',
        illustrationId: 103258,
      },
      {
        id: 'lw-5',
        title: 'Die Gründung',
        narrative: 'Die Menschen kamen aus den Dörfern. Dort, wo der Turm stand, gründeten sie eine Stadt. Klagenfurt nannten sie sie — die Furt der Klage, denn noch lange hörte man nachts das Echo des Drachenschreis. Und wenn ihr heute durch Klagenfurt geht, schaut euch das Wappen an. Da ist er noch immer — der Lindwurm. Nicht als Warnung, sondern als Erinnerung. Daran, dass manchmal nicht die stärkste Waffe siegt, sondern der klügste Plan.',
        mood: 'friedlich',
        illustrationPrompt: 'Medieval village around a stone tower, coat of arms with dragon on banner, golden hour, villagers celebrating, pastoral watercolor',
        illustrationId: 103259,
        location: 'Klagenfurt',
      },
    ],
  },
  {
    id: 'sage-glocke',
    title: 'Die Glocke im Wörther See',
    subtitle: 'Von der versunkenen Stadt und der traurigen Glocke',
    narrator: 'tschauka',
    cover: '🔔',
    color: '#4FC3F7',
    region: 'Wörther See',
    duration: '8 Min.',
    scenes: [
      {
        id: 'gl-1',
        title: 'Die prächtige Stadt',
        narrative: 'Es war einmal eine Stadt am Fuße der Berge, dort wo heute das Wasser des Wörther Sees glitzert. Türme aus weißem Stein, Brunnen aus poliertem Marmor, in den Gassen duftete es nach Honigbrot. Die Menschen trugen Seide und Samt, Gold blitzte an ihren Fingern. Aber ihre Herzen waren kalt wie der Stein ihrer Türme. Wenn ein Wanderer an ihre Tür klopfte, schickten sie ihn fort. Die Armen wurden ärmer, die Reichen reicher, und niemand sah nach oben, wo sich die Wolken zusammenzogen.',
        mood: 'friedlich',
        illustrationPrompt: 'Magnificent medieval city with white towers and marble fountains, wealthy people turning away a beggar, watercolor with gold and white tones, dark clouds gathering',
        illustrationId: 103248,
        videoId: 103267,
        location: 'Wörther See',
      },
      {
        id: 'gl-2',
        title: 'Der Mann mit dem Fass',
        narrative: 'An einem Herbstmorgen kam ein kleiner Mann durch das Stadttor. Grau war sein Haar, grau sein Mantel, grau seine Augen. Er trug ein Fass auf dem Rücken und stellte es mitten auf dem Marktplatz ab. "Ändert euren Weg. Öffnet eure Herzen. Denn wenn ihr es nicht tut, öffne ich den Hahn dieses Fasses." Die Leute lachten. Ein Kaufmann rief: "Dreh ihn auf, Alter!" Der kleine Mann sah sie an mit einem Blick, tief wie ein Brunnen ohne Boden. Dann nickte er — und drehte den Hahn auf.',
        mood: 'mystisch',
        illustrationPrompt: 'Small grey old man with barrel on medieval market square, laughing crowd mocking him, hand on tap, mystical grey mist, watercolor with grey and amber',
        illustrationId: 103252,
      },
      {
        id: 'gl-3',
        title: 'Die Flut',
        narrative: 'Das Rinnsal wurde ein Bach, der Bach ein Strom. Dann begann es zu regnen — als hätte jemand den Himmel aufgeschlitzt. Die Gassen füllten sich, die Keller liefen voll. Das Wasser stieg und stieg, und die Tore, die sie so oft vor Bittenden verschlossen hatten, ließen sich nicht mehr öffnen. Drei Tage regnete es. Als die Sonne am vierten Morgen brach, war die Stadt verschwunden. An ihrer Stelle lag ein See — still, blau und tief. Fische schwimmen nun durch die Gassen der versunkenen Paläste.',
        mood: 'dramatisch',
        illustrationPrompt: 'Dramatic flood, water from barrel and sky, medieval city sinking, underwater city with fish in palace windows, split watercolor above and below waterline',
        illustrationId: 103254,
      },
      {
        id: 'gl-4',
        title: 'Die traurige Glocke',
        narrative: 'Am Ufer gab es eine kleine Kapelle mit einer Glocke — klein und schlicht, mit einem Ton so rein wie der erste Schnee. Sie hatte die Flut überlebt. Doch als man die Orgel fortbrachte, verstand die Glocke das nicht. Für sie gehörten Orgel und Glocke zusammen wie Frage und Antwort. In jener Nacht löste sie sich vom Balken, rollte den Hügel hinab und glitt mit einem letzten, leisen Ton ins Wasser. Dort unten bewacht sie nun ein Riesenkrebs mit Scheren wie geschmiedetes Eisen.',
        mood: 'mystisch',
        illustrationPrompt: 'Chapel bell rolling down moonlit hill into lake, underwater bell among sunken towers guarded by giant crab, moonlight through water, watercolor in blues and silvers',
        illustrationId: 103257,
        location: 'Schloss Loretto',
      },
      {
        id: 'gl-5',
        title: 'Die Prophezeiung',
        narrative: 'Man sagt, eines Tages werden im Rosental Zwillingstiere geboren, vollkommen gleich wie ein Spiegelbild. Der Riesenkrebs wird vor Staunen seine Scheren öffnen, und ein mutiger Mensch wird die Glocke heben. Wenn sie wieder läutet, erscheint die Stadt ein zweites Mal — mit offenen Toren. Ob das geschehen wird? Ich weiß es nicht. Aber manchmal, an ganz stillen Abenden, wenn der See glatt ist wie ein Spiegel, kann man es hören. Ein leises Läuten aus der Tiefe. Wie eine Frage, die noch auf ihre Antwort wartet.',
        mood: 'friedlich',
        illustrationPrompt: 'Peaceful evening at Woerther See, still water reflecting mountains, twin lambs at shore, golden glow from underwater, watercolor in blues and golds',
        illustrationId: 103261,
        location: 'Wörther See',
      },
    ],
  },
  {
    id: 'sage-bergzwerg',
    title: 'Der Bergzwerg von Loben',
    subtitle: 'Vom Karfunkelstein und der falschen Frage',
    narrator: 'tschauko',
    cover: '⛏️',
    color: '#7CB342',
    region: 'Loben',
    duration: '6 Min.',
    scenes: [
      {
        id: 'bz-1',
        title: 'Das Klopfen im Berg',
        narrative: 'Tief in den Bergen bei Loben, wo der Fels dunkel ist und nach Eisen riecht, arbeiteten die Knappen im Stollen. Aber manchmal, in den tiefsten Gängen, wo die Laternen nur schwach flackerten, hörten sie ein Klopfen. Leise, regelmäßig, wie ein kleiner Hammer auf Stein. Es kam aus Gängen, in denen niemand arbeitete. "Das ist der Bergzwerg", flüsterten die alten Knappen. "Er klopft dort, wo das beste Erz liegt. Wo Silber wie Adern durch den Fels läuft und Gold in Nestern sitzt wie Eigelb in der Schale."',
        mood: 'mystisch',
        illustrationPrompt: 'Deep dark mine tunnel, flickering lantern, miners listening, mysterious tapping marks glowing in rock, watercolor in earth tones with amber light',
        illustrationId: 103250,
        location: 'Loben',
      },
      {
        id: 'bz-2',
        title: 'Die Falle mit Milch und Honig',
        narrative: 'Die Bergleute stellten eine Schale aus Ton in den tiefsten Stollen, gefüllt mit warmer Milch und goldenem Honig. Dann löschten sie die Laternen und warteten im Dunkeln. Es dauerte lange. Aber dann hörten sie kleine Schritte und ein genüssliches Schlürfen. Der Zwerg trank und trank, und die Gier war größer als die Vernunft. Als die Laternen wieder brannten, sahen sie ihn: klein wie ein Laib Brot, runzliges Gesicht, spitze Mütze, Augen wie frisch geschlagenes Erz — aber jetzt trüb und voll und müde.',
        mood: 'mystisch',
        illustrationPrompt: 'Tiny bearded dwarf with pointed cap drinking from clay bowl in dark mine, lantern light on face, sleepy expression, watercolor in honey and amber tones',
        illustrationId: 103253,
      },
      {
        id: 'bz-3',
        title: 'Die Antworten',
        narrative: 'Die Bergleute setzten den Zwerg auf einen Stein und stellten ihre Fragen. "Wo das beste Erz?" — "Im dritten Gang, hinter der Felsnase." "Wo Silber?" — "Folgt der nassen Wand — wo der Fels weint, da glänzt das Silber." "Und Gold?" — Ein müdes Lächeln. "Am Ende des Blindstollens, wo ihr aufgehört habt zu graben." Frage um Frage, und der Zwerg antwortete geduldig. Die Bergleute schrieben alles auf, glücklich und aufgeregt. Dann ließen sie ihn gehen.',
        mood: 'friedlich',
        illustrationPrompt: 'Small dwarf on rock in mine, eager miners taking notes on slate, dwarf pointing, warm candlelight, charming watercolor in golden tones',
        illustrationId: 103256,
      },
      {
        id: 'bz-4',
        title: 'Der Betrug und der Karfunkelstein',
        narrative: 'An der Felswand, halb schon im Stein verschwunden, drehte sich der Zwerg um. Seine Augen blitzten auf. "Es war alles gelogen! Jedes Wort!" Die Freude wich aus den Gesichtern der Bergleute. "Und das Dümmste ist", sagte der Zwerg leiser, "ihr habt nicht einmal die richtige Frage gestellt. Ich weiß, wo der Karfunkelstein liegt." Dann war er fort. Der Fels schloss sich hinter ihm. Sie stellten wieder Milch und Honig auf, Nacht für Nacht. Aber der Zwerg kam nie zurück. Nur das Klopfen hört man noch immer. Tick, tick, tick.',
        mood: 'dramatisch',
        illustrationPrompt: 'Dwarf half-merged with rock wall, mischievous gleaming eyes, shocked miners, mysterious red glow of Karfunkelstein deep in mountain, watercolor in reds and golds',
        illustrationId: 103260,
      },
    ],
  },
  {
    id: 'sage-teufelsbruecke',
    title: 'Die Sage der Teufelsbrücke',
    subtitle: 'Vom Hirten, der schlauer als der Teufel war',
    narrator: 'tschauko',
    cover: '😈',
    color: '#7CB342',
    region: 'Tscheppaschlucht',
    duration: '6 Min.',
    scenes: [
      {
        id: 'tb-1',
        title: 'Der Sturm',
        narrative: 'Es war einmal ein Hirte in den Bergen Kärntens. Er lebte mit seiner Herde am Rande der Tscheppaschlucht, wo das Wasser tief zwischen den Felsen rauscht. Eines Nachts kam ein furchtbarer Sturm. Der Wind heulte durch die Schlucht, der Regen peitschte gegen die Felsen — und als der Morgen kam, war die alte Brücke über die Schlucht eingestürzt. Nichts als zersplitterte Balken hingen noch über dem Abgrund.',
        mood: 'dramatisch',
        illustrationPrompt: 'A violent storm in an alpine gorge at night, an old wooden bridge collapsing under the force of wind and rain, lightning, splintered wood, dramatic watercolor with dark blues and grays',
        illustrationId: 103316,
        location: 'Tscheppaschlucht',
      },
      {
        id: 'tb-2',
        title: 'Die Not',
        narrative: 'Der Hirte stand am Rand der Schlucht und blickte hinüber. Auf der anderen Seite lagen die Weiden, auf denen seine Herde grasen musste, bevor der Winter kam. Ohne die Brücke gab es keinen Weg hinüber — der Umweg durch die Berge hätte Tage gedauert. Die Ziegen meckerten unruhig. Der alte Bock stampfte mit dem Huf. Der Hirte setzte sich auf einen Stein und dachte nach.',
        mood: 'friedlich',
        illustrationPrompt: 'A shepherd sitting on a rock at the edge of a deep alpine gorge, looking across at green meadows on the other side, a herd of goats behind him, broken bridge remains, contemplative mood, watercolor',
        illustrationId: 103317,
      },
      {
        id: 'tb-3',
        title: 'Der Deal',
        narrative: 'Da stieg Rauch aus dem Boden, und plötzlich stand der Teufel vor ihm. Groß, mit Hörnern und einem Grinsen, das nichts Gutes verhieß. „Ich sehe, du hast ein Problem, Hirte", sagte der Teufel. „Ich kann dir die Brücke in einer einzigen Nacht wieder aufbauen. Stärker als zuvor." Der Hirte sah ihn misstrauisch an. „Und was willst du dafür?" Der Teufel leckte sich die Lippen. „Die erste Seele, die über die Brücke geht."',
        mood: 'dunkel',
        illustrationPrompt: 'The Devil appearing before a shepherd in an alpine gorge, smoke rising from the ground, the Devil with horns and a cunning grin, the shepherd standing firm, dark dramatic watercolor with reds and blacks',
        illustrationId: 103318,
      },
      {
        id: 'tb-4',
        title: 'Die List',
        narrative: 'Der Hirte überlegte. Dann streckte er dem Teufel die Hand entgegen. „Abgemacht." Der Teufel lachte und machte sich ans Werk. Die ganze Nacht hörte man Donnern und Krachen aus der Schlucht. Als die Sonne aufging, stand eine neue Brücke über dem Abgrund — massiv, aus Stein, als wäre sie schon immer dort gewesen. Der Teufel wartete grinsend am anderen Ende. „Nun, Hirte. Wer geht als Erstes?"',
        mood: 'dramatisch',
        illustrationPrompt: 'A magnificent new stone bridge over an alpine gorge at sunrise, the Devil standing proudly at the far end with arms crossed, a shepherd with his herd on the near side, dramatic morning light, watercolor',
        illustrationId: 103319,
      },
      {
        id: 'tb-5',
        title: 'Der alte Bock',
        narrative: 'Der Hirte lächelte. Er drehte sich um, griff den ältesten Ziegenbock an den Hörnern und schob ihn auf die Brücke. Der Bock trabte gemächlich hinüber — direkt auf den Teufel zu. Der Teufel starrte den Bock an. Dann den Hirten. Sein Grinsen verschwand. „Das ist keine Menschenseele!", brüllte er. „Du hast die erste Seele verlangt", sagte der Hirte ruhig. „Hier ist sie."',
        mood: 'dramatisch',
        illustrationPrompt: 'An old billy goat walking across a stone bridge toward a furious Devil, the shepherd smiling cleverly behind, alpine gorge setting, humorous yet dramatic watercolor',
        illustrationId: 103320,
      },
      {
        id: 'tb-6',
        title: 'Die Höllenfahrt',
        narrative: 'Man erzählt sich, dass der Teufel so wütend war, dass er auf den Bock sprang und direkt auf ihm in die Hölle ritt. Seit diesem Tag steht die Brücke in der Tscheppaschlucht — gebaut vom Teufel, überlistet von einem Hirten. Und wenn du genau hinschaust, siehst du vielleicht noch die Hufabdrücke des alten Bocks im Stein.',
        mood: 'mystisch',
        illustrationPrompt: 'The Devil riding angrily on an old goat disappearing into flames and smoke beneath a stone bridge in an alpine gorge, the shepherd waving goodbye with a smile, magical humorous watercolor',
        illustrationId: 103321,
        location: 'Tscheppaschlucht',
      },
    ],
  },
]
